/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Modal from '../../../../../components/Modal/Modal';
import {
   PrimaryButton,
   CtaButton,
   GhostButton,
} from '../../../../../styles/ui/Button';
import { FormGrid } from '../../../../../styles/layout/FormGrid';
import { Field, RequiredMark } from '../../../../../styles/forms/Field';
import { submitInterviewResult } from '../../../../../services/hr/recruitment/interviewsApi';
import {
   INTERVIEW_RESULT_LABEL,
   resultOptionsForRound,
   structuredNotesFieldsForRound,
} from '../../constants';
import { readApiError, recruitmentErrorMessage } from '../../utils/apiError';

// ---------------------------------------------------------------------------
// Registrar o resultado da entrevista ativa (PATCH /interviews/:id/result), em
// WIZARD passo-a-passo: Desfecho → 1 passo por campo do roteiro → Observações.
// Navegação DINÂMICA: cada bolinha do stepper é clicável e leva a qualquer passo
// (livre, independente do preenchimento); a bolinha fica AZUL quando o campo tem
// conteúdo e o passo atual ganha um ANEL. Subtítulo por campo (constants.hint)
// ajuda usuários novos. O subconjunto de `result` e os campos do roteiro mudam
// por RODADA (E1 ≠ E2). Registrar muda o stage no backend.
// ---------------------------------------------------------------------------

const ink = ({ theme }) => theme?.colors?.textPrimary ?? '#1f1f18';
const accent = ({ theme }) => theme?.colors?.actionWrite ?? '#4098ef';

const FieldError = styled.span`
   font-size: 11px;
   color: ${({ theme }) => theme?.colors?.danger ?? '#b71c1c'};
   margin-top: 2px;
`;

const Stepper = styled.div`
   display: flex;
   align-items: center;
   margin-bottom: 12px;
`;

// Bolinha clicável (navegação livre). Azul quando o passo tem conteúdo; anel no
// passo atual.
const StepBadge = styled.button`
   flex: 0 0 auto;
   appearance: none;
   -webkit-appearance: none;
   border: 0;
   cursor: pointer;
   font-family: inherit;
   width: 26px;
   height: 26px;
   border-radius: 50%;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 12px;
   font-weight: 800;
   background: ${({ $filled }) => ($filled ? accent : 'rgba(0,0,0,0.09)')};
   color: ${({ $filled }) => ($filled ? '#fff' : '#6b7280')};
   box-shadow: ${({ $active }) =>
      $active ? '0 0 0 4px rgba(64, 152, 239, 0.28)' : 'none'};
   transition:
      background 0.15s ease,
      box-shadow 0.15s ease;

   &:hover {
      background: ${({ $filled }) => ($filled ? accent : 'rgba(0,0,0,0.16)')};
   }
   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 4px rgba(64, 152, 239, 0.4);
   }
`;

const StepLine = styled.span`
   flex: 1 1 auto;
   min-width: 8px;
   height: 2px;
   margin: 0 6px;
   background: ${({ $done }) => ($done ? accent : 'rgba(0,0,0,0.10)')};
   transition: background 0.15s ease;
`;

const StepMeta = styled.p`
   margin: 0 0 2px;
   font-size: 11px;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.03em;
   color: ${accent};
`;

const StepTitle = styled.p`
   margin: 0 0 6px;
   font-size: 1rem;
   font-weight: 800;
   color: ${ink};
`;

// Rede de segurança: mesmo com passos curtos, o conteúdo nunca ultrapassa a
// viewport (rola internamente em telas baixas).
const StepContent = styled.div`
   max-height: 52vh;
   overflow-y: auto;
   padding-right: 4px;
`;

const StepHint = styled.p`
   margin: 0 0 14px;
   font-size: 12.5px;
   line-height: 1.45;
   color: #6b7280;
`;

const Summary = styled.div`
   margin-top: 14px;
   padding: 12px 14px;
   border-radius: 10px;
   background: rgba(24, 95, 165, 0.06);
   font-size: 13px;

   .s-label {
      color: #6b7280;
   }
   .s-value {
      font-weight: 800;
   }
`;

function buildInitialNotes(round) {
   const fields = structuredNotesFieldsForRound(round);
   const initial = {};
   fields.forEach((field) => {
      initial[field.key] = '';
   });
   return initial;
}

export default function InterviewResultModal({
   isOpen,
   onClose,
   interview,
   onSaved,
}) {
   const round = interview?.round ?? 1;
   const resultOptions = resultOptionsForRound(round);
   const structuredFields = structuredNotesFieldsForRound(round);

   // Passos dinâmicos: Desfecho + 1 por campo do roteiro + Observações.
   const steps = [
      { key: 'outcome' },
      ...structuredFields.map((field) => ({ key: `note-${field.key}`, field })),
      { key: 'notes' },
   ];
   const totalSteps = steps.length;
   const lastIndex = totalSteps - 1;

   const [step, setStep] = useState(0);
   const [attended, setAttended] = useState('');
   const [result, setResult] = useState('');
   const [structuredNotes, setStructuredNotes] = useState(() =>
      buildInitialNotes(round),
   );
   const [notes, setNotes] = useState('');
   const [errors, setErrors] = useState({});
   const [isSubmitting, setIsSubmitting] = useState(false);

   useEffect(() => {
      if (isOpen && interview) {
         setStep(0);
         setAttended('');
         setResult('');
         setStructuredNotes(buildInitialNotes(interview.round));
         setNotes('');
         setErrors({});
         setIsSubmitting(false);
      }
   }, [isOpen, interview]);

   const updateNote = (key, value) => {
      setStructuredNotes((prev) => ({ ...prev, [key]: value }));
   };

   // Um passo está "preenchido" (bolinha azul) quando tem conteúdo.
   const isStepFilled = (idx) => {
      if (idx === 0) return Boolean(result) || attended !== '';
      if (idx === lastIndex) return notes.trim() !== '';
      const field = structuredFields[idx - 1];
      return Boolean(structuredNotes[field?.key]?.trim());
   };

   // Navegação livre — Próximo/Voltar/bolinha não travam no preenchimento; a
   // exigência do resultado é validada só no submit.
   const goNext = () => setStep((s) => Math.min(s + 1, lastIndex));
   const goBack = () => setStep((s) => Math.max(s - 1, 0));

   const handleConfirm = async () => {
      if (isSubmitting) return;
      if (!result) {
         setStep(0);
         setErrors({ result: 'Selecione o resultado da entrevista.' });
         return;
      }

      const filledNotes = {};
      Object.entries(structuredNotes).forEach(([key, value]) => {
         if (value.trim()) filledNotes[key] = value.trim();
      });

      let attendedValue;
      if (attended === 'true') attendedValue = true;
      else if (attended === 'false') attendedValue = false;

      setIsSubmitting(true);
      setErrors({});
      try {
         const { message } = await submitInterviewResult(interview.id, {
            attended: attendedValue,
            result,
            structuredNotes: Object.keys(filledNotes).length
               ? filledNotes
               : undefined,
            notes: notes.trim() || undefined,
         });
         toast.success(message);
         onSaved?.();
         onClose?.();
      } catch (err) {
         const { fieldErrors } = readApiError(err);
         if (fieldErrors.result) {
            setStep(0);
            setErrors(fieldErrors);
         } else if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
         } else {
            toast.error(
               recruitmentErrorMessage(err, 'Erro ao registrar o resultado.'),
            );
         }
      } finally {
         setIsSubmitting(false);
      }
   };

   const isOutcomeStep = step === 0;
   const isNotesStep = step === lastIndex;
   const currentField =
      !isOutcomeStep && !isNotesStep ? structuredFields[step - 1] : null;

   const footer = (
      <>
         {isOutcomeStep ? (
            <GhostButton type="button" onClick={onClose} disabled={isSubmitting}>
               Cancelar
            </GhostButton>
         ) : (
            <GhostButton type="button" onClick={goBack} disabled={isSubmitting}>
               Voltar
            </GhostButton>
         )}
         {isNotesStep ? (
            <PrimaryButton
               type="button"
               onClick={handleConfirm}
               disabled={isSubmitting}
            >
               {isSubmitting ? 'Registrando…' : 'Registrar resultado'}
            </PrimaryButton>
         ) : (
            <CtaButton type="button" onClick={goNext} disabled={isSubmitting}>
               Próximo
            </CtaButton>
         )}
      </>
   );

   return (
      <Modal
         isOpen={isOpen}
         onClose={onClose}
         title={`Resultado — Entrevista ${round}`}
         subtitle="O resultado avança ou encerra o candidato no funil."
         maxWidth="620px"
         footer={footer}
      >
         <Stepper>
            {steps.map((s, idx) => {
               const filled = isStepFilled(idx);
               const active = idx === step;
               return (
                  <React.Fragment key={s.key}>
                     {idx > 0 && <StepLine $done={isStepFilled(idx - 1)} />}
                     <StepBadge
                        type="button"
                        $filled={filled}
                        $active={active}
                        onClick={() => setStep(idx)}
                        aria-label={`Ir para o passo ${idx + 1} de ${totalSteps}`}
                        aria-current={active ? 'step' : undefined}
                        disabled={isSubmitting}
                     >
                        {filled && !active ? '✓' : idx + 1}
                     </StepBadge>
                  </React.Fragment>
               );
            })}
         </Stepper>

         <StepMeta>
            Passo {step + 1} de {totalSteps}
         </StepMeta>

         <StepContent>
            {isOutcomeStep && (
               <>
                  <StepTitle>Desfecho</StepTitle>
                  <StepHint>
                     Registre se o candidato compareceu e o resultado da
                     entrevista.
                  </StepHint>
                  <FormGrid>
                     <Field className="col-6">
                        <label htmlFor="result-attended">Compareceu?</label>
                        <select
                           id="result-attended"
                           value={attended}
                           onChange={(e) => setAttended(e.target.value)}
                           disabled={isSubmitting}
                        >
                           <option value="">Não informado</option>
                           <option value="true">Sim</option>
                           <option value="false">Não</option>
                        </select>
                     </Field>

                     <Field className="col-6">
                        <label htmlFor="result-result">
                           Resultado
                           <RequiredMark>*</RequiredMark>
                        </label>
                        <select
                           id="result-result"
                           value={result}
                           onChange={(e) => {
                              setResult(e.target.value);
                              setErrors((prev) => ({
                                 ...prev,
                                 result: undefined,
                              }));
                           }}
                           disabled={isSubmitting}
                           aria-invalid={!!errors.result || undefined}
                        >
                           <option value="">Selecione…</option>
                           {resultOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                 {option.label}
                              </option>
                           ))}
                        </select>
                        {errors.result && (
                           <FieldError>{errors.result}</FieldError>
                        )}
                     </Field>
                  </FormGrid>
               </>
            )}

            {currentField && (
               <>
                  <StepTitle>{currentField.label}</StepTitle>
                  <StepHint>{currentField.hint ?? 'Campo opcional.'}</StepHint>
                  <FormGrid>
                     <Field className="col-12">
                        <textarea
                           id={`result-note-${currentField.key}`}
                           aria-label={currentField.label}
                           rows={6}
                           value={structuredNotes[currentField.key] ?? ''}
                           onChange={(e) =>
                              updateNote(currentField.key, e.target.value)
                           }
                           disabled={isSubmitting}
                        />
                     </Field>
                  </FormGrid>
               </>
            )}

            {isNotesStep && (
               <>
                  <StepTitle>Observações</StepTitle>
                  <StepHint>
                     Impressão geral e recomendação sobre o candidato — contexto
                     que ajude a decisão.
                  </StepHint>
                  <FormGrid>
                     <Field className="col-12">
                        <textarea
                           id="result-notes"
                           aria-label="Observações gerais"
                           rows={5}
                           value={notes}
                           onChange={(e) => setNotes(e.target.value)}
                           disabled={isSubmitting}
                           placeholder="Comentário livre sobre a entrevista..."
                        />
                     </Field>
                  </FormGrid>
                  <Summary>
                     <span className="s-label">Resultado a registrar: </span>
                     <span className="s-value">
                        {INTERVIEW_RESULT_LABEL[result] ?? '—'}
                     </span>
                  </Summary>
               </>
            )}
         </StepContent>
      </Modal>
   );
}

InterviewResultModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   interview: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      round: PropTypes.number,
   }),
   onSaved: PropTypes.func,
};
